import dayjs from 'dayjs';

import type {
  SaleInvoice,
  SaleInvoiceSummary,
  SaleItemSuggestion,
  SaleLineInput,
  SalePayment,
  SalePaymentInput,
  SaveSaleInput,
  SellableGarment,
} from '../api/saleInvoices';
import type { TaxDocument } from '../api/taxDocuments';
import { paymentMethodLabel } from '../bookings/paymentWords';
import { filledParts, type SplitPart } from '../cash/split';
import { formatAmount } from '../format/money';
import type {
  AvailabilityBlockType,
  GarmentSaleRefusal,
  PaymentMethod,
  SaleInvoiceStatus,
} from '../generated/enums';
import type { StatusTone } from '../vocabulary';

/**
 * The pure half of the Sell page (step 5 of `docs/Purchase_Sale_Outstanding_Design.md`).
 *
 * <p>
 * <strong>No tax arithmetic here.</strong> Prices are typed GST-inclusive and the server's preview
 * says what they come to; this module only holds what the counter typed, turns it into the request,
 * and says what stops it being sent. The one sum it does — what the payments add up to — is money
 * the clerk counted, not a tax figure.
 * </p>
 */

/** One line on the counter, figures as typed so a half-typed number is not lost. */
export interface CartLine {
  /** Stable while the cart is open; never sent. */
  key: string;
  kind: 'Garment' | 'Other';
  itemUnitId: number | null;
  serialCode: string | null;
  /** The shop's own number, shown beside the code; never sent. */
  referenceNumber?: string | null;
  description: string;
  hsnCode: string;
  /** From the catalogue: the field is shown, not editable. */
  catalogueHsn: string | null;
  /** From the catalogue: no rate is typed. Null: the counter types one. */
  catalogueRate: number | null;
  quantity: string;
  unitPrice: string;
  ratePct: string;
  /** Off this line, GST included: `200` is ₹200, `5%` is five per cent. */
  discount: string;
  photoUrl: string | null;
}

export interface CartPayment {
  key: string;
  method: Exclude<PaymentMethod, 'Adjustment'>;
  amount: string;
  reference: string;
  /** The account it goes into (split payments); absent or null is the method's default. */
  cashAccountId?: number | null;
}

/** A typed figure: empty is "not given" (null), anything unreadable NaN for the checks to name. */
export function typedFigure(text: string): number | null {
  const cleaned = text.replace(/[,\s₹%]/g, '');

  return cleaned === '' ? null : Number(cleaned);
}

/**
 * The counter's button (canvas "Sales flow", 2026-10-25), on both clients: "Sell for ₹2,100" when
 * what is being paid covers the bill, "Take ₹1,000 · save the bill" when part is paid, and "Save the
 * bill · ₹2,100 owed" when nothing is.
 */
export function sellWords(total: number | null, paid: number): string {
  if (total === null) {
    return 'Sell';
  }

  const rupee = (value: number) => `₹${formatAmount(value)}`;

  return paid >= total
    ? `Sell for ${rupee(total)}`
    : paid > 0
      ? `Take ${rupee(paid)} · save the bill`
      : `Save the bill · ${rupee(total)} owed`;
}

/**
 * The codes a scanned or typed tag could be, in the order to ask: as written first — an older tag
 * such as `TUX_NAVY-40-NAVY-SLIM-001` keeps its three digits, and reading it the short way would
 * make it `…-01` — then the short reading (`bjb36-1` → `BJB-36-01`) when it differs.
 */
export function garmentCodeCandidates(typed: string, shortReading: string | null): string[] {
  const asWritten = typed.trim().toUpperCase();

  if (!asWritten) {
    return [];
  }

  return shortReading && shortReading !== asWritten ? [asWritten, shortReading] : [asWritten];
}

/**
 * A scanned garment as a line — HSN and rate from the catalogue where it has them, and the design's
 * sale price filled in (owner, 2026-10-22), which the counter can still change.
 */
export function garmentLine(garment: SellableGarment, key: string): CartLine {
  return {
    key,
    kind: 'Garment',
    itemUnitId: garment.itemUnitId,
    serialCode: garment.serialCode,
    ...(garment.referenceNumber ? { referenceNumber: garment.referenceNumber } : {}),
    description: garment.itemName || garment.description,
    hsnCode: garment.hsnCode ?? '',
    catalogueHsn: garment.hsnCode,
    catalogueRate: garment.saleRatePct,
    quantity: '1',
    unitPrice: garment.salePrice === null ? '' : String(garment.salePrice),
    ratePct: garment.saleRatePct === null ? '' : String(garment.saleRatePct),
    discount: '',
    photoUrl: garment.photoUrl,
  };
}

/**
 * A stole, a brooch — named by the clerk, or picked from what was sold before, which fills in its
 * last price, HSN and rate.
 */
export function otherLine(key: string, from?: SaleItemSuggestion): CartLine {
  return {
    key,
    kind: 'Other',
    itemUnitId: null,
    serialCode: null,
    description: from?.description ?? '',
    hsnCode: from?.hsnCode ?? '',
    catalogueHsn: null,
    catalogueRate: null,
    quantity: '1',
    unitPrice: from ? String(from.unitPrice) : '',
    ratePct: from?.ratePct === null || from?.ratePct === undefined ? '' : String(from.ratePct),
    discount: '',
    photoUrl: null,
  };
}

/** A line's typed discount as the server takes it: `5%` a percentage, `200` rupees, empty none. */
export function lineDiscountInput(text: string): {
  discountPct: number | null;
  discountAmount: number | null;
} {
  const value = typedFigure(text);

  if (value === null || Number.isNaN(value) || value <= 0) {
    return { discountPct: null, discountAmount: null };
  }

  return text.includes('%')
    ? { discountPct: value, discountAmount: null }
    : { discountPct: null, discountAmount: value };
}

/**
 * What the server is sent for one line. A rate the catalogue holds is never sent — it would only be
 * refused if it differed — and neither is an HSN it holds.
 */
export function lineInput(line: CartLine): SaleLineInput {
  const garment = line.kind === 'Garment';

  return {
    kind: line.kind,
    itemUnitId: garment ? line.itemUnitId : null,
    description: garment ? null : line.description.trim() || null,
    hsnCode: line.catalogueHsn !== null ? null : line.hsnCode.trim() || null,
    quantity: garment ? 1 : (typedFigure(line.quantity) ?? 0),
    unitPrice: typedFigure(line.unitPrice) ?? 0,
    ratePct: line.catalogueRate !== null ? null : typedFigure(line.ratePct),
    ...lineDiscountInput(line.discount),
  };
}

const isBad = (value: number | null) => value !== null && (Number.isNaN(value) || value < 0);

/**
 * What stops the cart being previewed at all — the checks the clerk can fix on the line itself.
 * A missing rate on a registered shop is the server's to say (a bill of supply needs none), so it is
 * not here.
 */
export function cartProblems(lines: readonly CartLine[]): string[] {
  const problems: string[] = [];

  if (lines.length === 0) {
    problems.push('Scan a garment or add an item.');
  }

  lines.forEach((line, index) => {
    const name =
      line.kind === 'Garment'
        ? (line.serialCode ?? `Line ${index + 1}`)
        : line.description.trim() || `Line ${index + 1}`;
    const price = typedFigure(line.unitPrice);

    if (price === null || Number.isNaN(price) || price <= 0) {
      problems.push(`${name}: type its price.`);
    }

    if (line.kind === 'Other') {
      if (!line.description.trim()) {
        problems.push(`Line ${index + 1}: name what is being sold.`);
      }

      const quantity = typedFigure(line.quantity);

      if (quantity === null || Number.isNaN(quantity) || quantity <= 0 || quantity > 9999) {
        problems.push(`${name}: a quantity between 1 and 9999.`);
      }
    }

    const rate = line.catalogueRate === null ? typedFigure(line.ratePct) : null;

    if (isBad(rate) || (rate !== null && rate > 100)) {
      problems.push(`${name}: a GST rate between 0 and 100.`);
    }

    const discount = typedFigure(line.discount);

    if (isBad(discount) || (discount !== null && line.discount.includes('%') && discount > 100)) {
      problems.push(`${name}: a discount in rupees, or a percentage up to 100%.`);
    }
  });

  return problems;
}

/** The discount as the server takes it: a percentage wins, a flat sum otherwise, empty is none. */
export function discountInput(mode: 'pct' | 'amount', text: string) {
  const value = typedFigure(text);

  if (value === null || Number.isNaN(value) || value <= 0) {
    return { discountPct: null, discountAmount: null };
  }

  return mode === 'pct'
    ? { discountPct: value, discountAmount: null }
    : { discountPct: null, discountAmount: value };
}

/** What the payments typed add up to. */
export function paidSoFar(payments: readonly CartPayment[]): number {
  return payments.reduce((sum, p) => {
    const value = typedFigure(p.amount);

    return sum + (value !== null && !Number.isNaN(value) && value > 0 ? value : 0);
  }, 0);
}

/**
 * What stops the sale being saved, given the server's total: no more taken than it comes to, and a
 * customer to owe whatever is left — the server's own refusals, said before Save.
 */
export function paymentProblems(
  total: number | null,
  payments: readonly CartPayment[],
  customerId: number | null,
): string[] {
  const problems: string[] = [];

  payments.forEach((payment, index) => {
    const value = typedFigure(payment.amount);

    if (value !== null && (Number.isNaN(value) || value < 0)) {
      problems.push(`Payment ${index + 1}: an amount above zero.`);
    }
  });

  if (total === null) {
    return problems;
  }

  const paid = paidSoFar(payments);

  if (paid > total + 0.004) {
    problems.push('The payments come to more than the sale.');
  } else if (paid < total - 0.004 && customerId === null) {
    problems.push('Something is left to pay — choose the customer who owes it.');
  }

  return problems;
}

function paymentInputs(payments: readonly CartPayment[]): SalePaymentInput[] {
  return payments
    .map((p) => ({
      method: p.method,
      amount: typedFigure(p.amount) ?? 0,
      reference: p.reference.trim() || null,
      cashAccountId: p.cashAccountId ?? null,
    }))
    .filter((p) => !Number.isNaN(p.amount) && p.amount > 0);
}

/**
 * The counter's payments from its account boxes (canvas "Money in, by account", 2026-11-01): one
 * payment per box with money in it, naming its account — so `paidSoFar`, `paymentProblems` and the
 * request read them as they read typed payments, and the sale saves them in one go with the bill.
 */
export function cartFromParts(parts: readonly SplitPart[]): CartPayment[] {
  return filledParts(parts).map((part) => ({
    key: `account-${part.accountId}`,
    method: part.method === 'Adjustment' ? 'Cash' : part.method,
    amount: part.amount,
    reference: part.reference,
    cashAccountId: part.accountId,
  }));
}

/** `POST /api/sale-invoice` (and its preview, which ignores the payments). */
export function saleRequest(input: {
  customerId: number | null;
  lines: readonly CartLine[];
  discount: { discountPct: number | null; discountAmount: number | null };
  payments: readonly CartPayment[];
  notes: string;
}): SaveSaleInput {
  return {
    customerId: input.customerId,
    lines: input.lines.map(lineInput),
    ...input.discount,
    payments: paymentInputs(input.payments),
    notes: input.notes.trim() || null,
  };
}

/* ------------------------------------------------------------------ words */

const refusalWords: Record<Exclude<GarmentSaleRefusal, 'None'>, string> = {
  AlreadySold: 'Already sold.',
  Gone: 'Recorded as lost or retired.',
  AwaitingInspection: 'Back from a hire and not inspected yet — inspect it first.',
  Held: 'Booked, held, out, or at the laundry or tailor from today on — it cannot be sold while anything holds it.',
};

/** Why a scanned garment cannot go on the sale, or null when it can. */
export function refusalSentence(refusal: GarmentSaleRefusal): string | null {
  return refusal === 'None' ? null : refusalWords[refusal];
}

export const refusals = Object.keys(refusalWords) as Exclude<GarmentSaleRefusal, 'None'>[];

/** What holds a garment, said the way a clerk would — the dates come from the server. */
const heldWords: Record<AvailabilityBlockType, (from: string, to: string) => string> = {
  Booking: (from, to) => `Booked ${from}–${to}`,
  Hold: (from, to) => `Held for a customer ${from}–${to}`,
  Cleaning: (_, to) => `At the laundry till ${to}`,
  Alteration: (_, to) => `At the tailor till ${to}`,
  Maintenance: (_, to) => `In repair till ${to}`,
  Transit: (_, to) => `On its way to another branch till ${to}`,
};

export const heldKinds = Object.keys(heldWords) as AvailabilityBlockType[];

/**
 * The short line under a garment in the search — why it cannot go, with dates where something holds
 * it — or null when it can be sold.
 */
export function garmentNote(
  garment: Pick<SellableGarment, 'refusal' | 'heldFor' | 'heldFrom' | 'heldTo'>,
): string | null {
  if (garment.refusal === 'Held' && garment.heldFor && garment.heldFrom && garment.heldTo) {
    const day = (value: string) => dayjs(value).format('D MMM');

    return heldWords[garment.heldFor](day(garment.heldFrom), day(garment.heldTo));
  }

  return refusalSentence(garment.refusal);
}

const statusWords: Record<SaleInvoiceStatus, { label: string; tone: StatusTone }> = {
  Unpaid: { label: 'Unpaid', tone: 'warning' },
  PartPaid: { label: 'Part paid', tone: 'warning' },
  Paid: { label: 'Paid', tone: 'positive' },
  Cancelled: { label: 'Cancelled', tone: 'neutral' },
};

export function saleStatusLabel(status: SaleInvoiceStatus): string {
  return statusWords[status].label;
}

export function saleStatusTone(status: SaleInvoiceStatus): StatusTone {
  return statusWords[status].tone;
}

export const saleStatuses = Object.keys(statusWords) as SaleInvoiceStatus[];

/**
 * Whether the server will cancel this sale, so the button is never offered for a refusal. A tax
 * invoice only while unpaid — money taken is a credit note's to undo, and that policy is open. A
 * bill of supply charged no GST, so even a paid one cancels, its money refunded (owner, 2026-10-22).
 */
export function canCancelSale(
  sale: Pick<SaleInvoice, 'status' | 'payments' | 'isBillOfSupply'>,
): boolean {
  return sale.status !== 'Cancelled' && (sale.isBillOfSupply || sale.payments.length === 0);
}

/** The refund a cancel would make — every payment, the way it came. */
export function refundOnCancel(sale: Pick<SaleInvoice, 'payments'>): number {
  return cents(sale.payments.reduce((sum, p) => sum + p.amount, 0));
}

/* ------------------------------------------------------------------ printing */

/**
 * A sale as the A4 tax invoice sheet reads a document — so a sale prints on the rental invoice's
 * own layout rather than a second copy of it. Every figure is the server's frozen one; the rate-wise
 * summary only groups the lines' own, as the server's summary of a rental document does.
 */
export function saleAsTaxDocument(sale: SaleInvoice): TaxDocument {
  const byRate = new Map<
    number,
    { taxableValue: number; cgstAmount: number; sgstAmount: number; igstAmount: number }
  >();

  for (const line of sale.lines) {
    const row = byRate.get(line.ratePct) ?? {
      taxableValue: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
    };

    byRate.set(line.ratePct, {
      taxableValue: cents(row.taxableValue + line.taxableValue),
      cgstAmount: cents(row.cgstAmount + line.cgstAmount),
      sgstAmount: cents(row.sgstAmount + line.sgstAmount),
      igstAmount: cents(row.igstAmount + line.igstAmount),
    });
  }

  return {
    id: sale.id,
    documentNumber: sale.documentNumber,
    kind: 'Invoice',
    status: sale.status === 'Cancelled' ? 'Cancelled' : 'Issued',
    rentalOrderId: 0,
    bookingNumber: '',
    againstDocumentId: null,
    againstDocumentNumber: null,
    documentDate: sale.invoiceDate,
    placeOfSupply: sale.placeOfSupply,
    isInterState: sale.isInterState,
    isBillOfSupply: sale.isBillOfSupply,
    supplierGstin: sale.supplierGstin,
    supplierStateCode: sale.supplierStateCode,
    supplierName: sale.supplierName,
    supplierAddress: sale.supplierAddress,
    customerName: sale.customerName,
    customerGstin: sale.customerGstin,
    customerStateCode: sale.customerStateCode,
    customerAddress: sale.customerAddress,
    customerMobile: sale.customerMobile,
    taxableValue: sale.taxableValue,
    cgstAmount: sale.cgstAmount,
    sgstAmount: sale.sgstAmount,
    igstAmount: sale.igstAmount,
    roundOff: sale.roundOff,
    documentTotal: sale.invoiceTotal,
    reason: sale.cancelReason,
    cancelledOn: sale.cancelledOn,
    lines: sale.lines.map((line) => ({
      id: line.id,
      rentalOrderItemId: null,
      kind: 'Sale',
      description: line.description,
      sacCode: null,
      hsnCode: line.hsnCode,
      quantity: line.quantity,
      unitValue: line.unitValue,
      grossValue: line.grossValue,
      discountAmount: line.discountAmount,
      taxableValue: line.taxableValue,
      ratePct: line.ratePct,
      cgstAmount: line.cgstAmount,
      sgstAmount: line.sgstAmount,
      igstAmount: line.igstAmount,
      lineTotal: line.lineTotal,
      sortOrder: line.sortOrder,
    })),
    rateSummary: [...byRate.entries()]
      .sort(([a], [b]) => a - b)
      .map(([ratePct, sums]) => ({ ratePct, ...sums })),
  };
}

/** Back to whole paise after adding frozen figures, which binary floating point does not keep. */
const cents = (value: number) => Math.round(value * 100) / 100;

/**
 * A register row in two lines (canvas "Sales flow", 2026-10-25): what was sold — the first two
 * names, then how many more — and under it the garments' tag codes, or how many other items. A
 * garment line reads "Name (CODE)"; the code is said once, underneath.
 */
export function soldWords(row: Pick<SaleInvoiceSummary, 'items' | 'garmentCodes'>): {
  what: string;
  codes: string;
} {
  const names = row.items.map((item) =>
    row.garmentCodes.reduce((name, code) => name.replace(` (${code})`, ''), item),
  );
  const what =
    names.length <= 2
      ? names.join(', ')
      : `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
  const others = row.items.length - row.garmentCodes.length;
  const otherWords = others === 0 ? null : `${others} other item${others === 1 ? '' : 's'}`;

  return {
    what: what || '—',
    codes: [...row.garmentCodes, otherWords].filter(Boolean).join(' · '),
  };
}

/** "UPI + Cash", in the order the money came; a dash when nothing was taken. */
export function paidByWords(row: Pick<SaleInvoiceSummary, 'paidBy'>): string {
  return row.paidBy.map((method) => paymentMethodLabel[method]).join(' + ') || '—';
}

/**
 * Where a payment landed: the account the ledger names (split payments), else — an older API, or no
 * row posted — the method's default: cash in the till, everything else in the bank.
 */
export function landsIn(payment: Pick<SalePayment, 'method' | 'cashAccountName'>): string {
  const named = payment.cashAccountName?.trim();

  if (named) {
    return named;
  }

  return payment.method === 'Cash' ? 'the till' : 'the bank';
}
